import type { Member, PlanItem, Role, TaskDetail, TaskRow, Team, TeamCharter } from '@/api'

/**
 * What an `os_change` plan item proposes, read as a change to the record it names (#239).
 *
 * An `update_*` item is laid over the record: the apply changes the fields it names and leaves every
 * other one exactly as it was. The CEO signing it off needs the current wording beside the proposed
 * one for the fields that change, and the rest shown as staying, never as removed.
 * A `create_task` item has nothing to compare against, so it has no diff at all and keeps its plain
 * field list. A member, role or team create (#1297) is read against nothing instead: every field it
 * carries reads as added, so the CEO approving a hire sees the same current-beside-proposed rows a
 * team update shows. A delete reads the record's current values with nothing proposed.
 */

/** What this proposal does to one field: `unchanged` still renders, muted, so nothing looks dropped. */
export type PlanDiffStatus = 'added' | 'removed' | 'changed' | 'unchanged'

/** One field of the record, as it is now and as the proposal would leave it, already formatted for reading. */
export type PlanDiffField = {
    key: string
    label: string
    status: PlanDiffStatus
    /** The value today, or `ABSENT_VALUE` when the record does not carry this field. */
    current: string
    /** The value the proposal would leave, or `ABSENT_VALUE` when the proposal drops the field. */
    proposed: string
}

/** What a cell shows when there is no value on that side of the change. */
export const ABSENT_VALUE = '—'

/** What the CEO needs to judge a proposed task, plus whatever a role/team/member item carries. */
const FIELD_LABELS: Record<string, string> = {
    name: 'Name',
    goal: 'Goal',
    metric: 'Metric',
    guardrails: 'Guardrails',
    tools: 'Tools',
    toolSlots: 'Tool slots',
    inputs: 'Inputs',
    assigneeRole: 'Role',
    assigneeRoleHandle: 'Role',
    memberHandle: 'Member',
    roleHandle: 'Role',
    teamHandle: 'Team',
    skill: 'Skill',
    skillMd: 'Skill',
    successors: 'Hands off to',
    finishesOn: 'Finishes the work on',
    kind: 'Kind',
    slug: 'Slug',
    schedule: 'Schedule',
    email: 'Email',
    persona: 'Persona',
    mission: 'Mission',
    purpose: 'Purpose',
    gates: 'Gates',
    spend: 'Spend',
    workspace: 'Workspace',
    lead: 'Lead',
    opportunity: 'Opportunity',
    success: 'Success looks like',
    kit: 'Skills, tools and assets',
    roles: 'Roles',
    teams: 'Teams',
    timeline: 'Timeline',
    status: 'Status',
}

/** Identity and wiring already shown in the item's own header, so neither list repeats them. */
export const HEADER_FIELDS = new Set(['type', 'handle', 'name', 'taskId', 'roleId', 'teamId', 'memberId'])

/** A field's heading: the one we have written for it, or its own key read as words. */
export function humanise(key: string): string {
    return FIELD_LABELS[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, first => first.toUpperCase())
}

/** One field of a plan item or of a current record, as a reader sees it rather than as JSON. */
export function formatValue(key: string, value: unknown): string {
    if (value === null || value === undefined) return ABSENT_VALUE
    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    if (Array.isArray(value)) {
        if (key === 'successors') {
            return value
                .map(entry => {
                    const successor = entry as Record<string, unknown>
                    const on = typeof successor.on === 'string' ? ` (on ${successor.on})` : ''
                    return `${String(successor.slug ?? '')}${on}`
                })
                .join(', ')
        }
        if (key === 'inputs') {
            return value
                .map(entry => {
                    const input = entry as Record<string, unknown>
                    return `${String(input.key ?? input.label ?? '')}${input.required ? ' (required)' : ''}`
                })
                .join(', ')
        }
        if (key === 'toolSlots') {
            return value
                .map(entry => {
                    const slot = entry as Record<string, unknown>
                    const label = typeof slot.label === 'string' && slot.label ? ` (${slot.label})` : ''
                    return `${String(slot.key ?? '')}${label}`
                })
                .join(', ')
        }
        return value.map(entry => (typeof entry === 'string' ? entry : JSON.stringify(entry))).join(', ')
    }
    const record = value as Record<string, unknown>
    if (typeof record.text === 'string') return record.text
    return JSON.stringify(value)
}

/**
 * Keys that share one label, most readable first. An item can carry more than one of them at once
 * (a role slug and the handle of a role the plan creates, say); only the first present one renders,
 * so a reviewer never sees two rows called "Role" with different values and no way to tell which counts.
 */
const SYNONYM_FIELDS: string[][] = [
    ['assigneeRole', 'assigneeRoleHandle'],
    // A plan item carries the skill as `skillMd`, the field `PlanApplyService` saves (#1282): read under one key
    // whichever names it, so the current skill and the proposed one meet as one "Skill" row.
    ['skillMd', 'skill'],
]

/** Every synonym key, pointing at the one key its group is read under. */
const CANONICAL_KEYS = new Map<string, string>(
    SYNONYM_FIELDS.flatMap(keys => keys.map(key => [key, keys[0] as string] as const)),
)

/**
 * The one key a field is read under, whichever synonym named it. A record always reports its role
 * as `assigneeRole`, while a proposal may name a new one by `assigneeRoleHandle`; without this the two
 * sides never meet and the reassignment reads as one "Role" removed beside another added.
 */
function canonicalKeyOf(key: string): string {
    return CANONICAL_KEYS.get(key) ?? key
}

/** The synonym keys this item should not render, because a better one for the same label is present. */
export function supersededFields(item: PlanItem): Set<string> {
    const superseded = new Set<string>()
    for (const keys of SYNONYM_FIELDS) {
        const present = keys.filter(key => {
            const value = (item as Record<string, unknown>)[key]
            return value !== null && value !== undefined && value !== ''
        })
        for (const key of present.slice(1)) superseded.add(key)
    }
    return superseded
}

/** A field as it will read, or null when there is nothing there to read: an empty list says nothing. */
function readable(key: string, value: unknown): string | null {
    if (value === null || value === undefined) return null
    const text = formatValue(key, value)
    return text === '' ? null : text
}

/** The fields of a record or an item worth showing, formatted, with the ones already in the header dropped. */
function fieldsOf(
    source: Record<string, unknown>,
    skip: Set<string>,
    hidden: Set<string> = HEADER_FIELDS,
): Map<string, string> {
    const fields = new Map<string, string>()
    for (const [key, value] of Object.entries(source)) {
        if (hidden.has(key) || skip.has(key)) continue
        const text = readable(key, value)
        if (text !== null) fields.set(key, text)
    }
    return fields
}

/** A plan item's own fields, as the plain (no diff) rendering lists them. */
export function planItemDetails(item: PlanItem): Array<{ key: string; label: string; value: string }> {
    return [...fieldsOf(item, supersededFields(item))].map(([key, value]) => ({ key, label: humanise(key), value }))
}

/**
 * What the item header carries, so this diff need not repeat it — `name` included, unless the
 * proposal renames the record. A rename is the one change the header hides: it prints the proposed
 * name and nothing else, so without this a CEO cannot tell "Support Rep" was ever called anything
 * else. A proposal that names no name leaves the record's own alone (`update_role` in
 * `plans.service.ts` writes `name` only when the item carries one), so it stays hidden rather than
 * reading as a field this update removes.
 */
function hiddenFieldsFor(item: PlanItem): Set<string> {
    if (readable('name', (item as Record<string, unknown>).name) === null) return HEADER_FIELDS
    const hidden = new Set(HEADER_FIELDS)
    hidden.delete('name')
    return hidden
}

/** The same fields read under one key per synonym group, so both sides of a diff line up on it. */
function canonicalised(fields: Map<string, string>): Map<string, string> {
    const canonical = new Map<string, string>()
    for (const [key, value] of fields) {
        const name = canonicalKeyOf(key)
        if (!canonical.has(name)) canonical.set(name, value)
    }
    return canonical
}

/** What the proposal does to one field, from the two readings of it. `undefined` is "nothing there". */
function statusOf(before: string | undefined, after: string | undefined): PlanDiffStatus {
    if (before === undefined) return 'added'
    if (after === undefined) return 'removed'
    return before === after ? 'unchanged' : 'changed'
}

/** Most significant first: what the proposal does is read before what it leaves alone. */
const STATUS_ORDER: PlanDiffStatus[] = ['changed', 'added', 'removed', 'unchanged']

/**
 * Whether the apply lays `item` over the record rather than replacing it. Every update does (`PlanApplyService`:
 * a new task version carries each field the item leaves out over from the version it replaces, and a role or team
 * update writes only what it names), so a field an update leaves out is read as unchanged, never as removed,
 * because approving it removes nothing.
 */
function laysOverRecord(item: PlanItem): boolean {
    return item.type.startsWith('update_')
}

/**
 * The fields an update names in order to empty them: a list given with nothing in it. The apply saves that empty
 * list over the record's own (`guardrails: []` clears the guardrails), so it is the one way an update removes a
 * field, and it must read as removed rather than as a field the item left alone.
 */
function emptiedFields(source: Record<string, unknown>): Set<string> {
    const emptied = new Set<string>()
    for (const [key, value] of Object.entries(source)) {
        if (Array.isArray(value) && value.length === 0) emptied.add(canonicalKeyOf(key))
    }
    return emptied
}

/** The creates read as a diff against nothing (#1297): every field a new member, role or team carries is added. */
const DIFFED_CREATES = new Set(['create_member', 'create_role', 'create_team'])

/** Whether `item` is a create read against an empty record, so each of its fields reads as added beside `—`. */
export function diffsAgainstNothing(item: PlanItem): boolean {
    return DIFFED_CREATES.has(item.type)
}

/**
 * A team item's `charter` answers read as fields of their own (`purpose`, `metric`, ...), the way
 * `currentTeamFields` reads a team's, so each answer lines up with the team's answer today rather than
 * the whole charter reading as one blob beside seven removed answers.
 */
function withCharterAnswers(item: PlanItem): Record<string, unknown> {
    const source = item as Record<string, unknown>
    const charter = source.charter
    if (typeof charter !== 'object' || charter === null || Array.isArray(charter)) return source
    const flat: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(source)) {
        if (key !== 'charter') flat[key] = value
    }
    return { ...flat, ...(charter as Record<string, unknown>) }
}

/**
 * The record an `update_*` or `delete_*` item would change, beside the item itself, field by field.
 *
 * An update is laid over the record (`laysOverRecord`), so a field the record has and the proposal
 * leaves out stays, and reads `unchanged`. Only a field the update names as an empty list is
 * `removed` (`emptiedFields`) — shown with its current wording rather than dropped from the page,
 * which is the case a CEO cannot catch by reading the proposal alone. A field only the proposal
 * carries is `added`, one both carry with the same reading is `unchanged`, and the rest are
 * `changed`. Values are compared as they read, so a reordered JSON blob that says the same thing
 * does not read as a change. A create compared with an empty record reads every field as added; a
 * delete, which proposes nothing, reads every current field as removed.
 */
export function diffPlanItem(item: PlanItem, current: Record<string, unknown>): PlanDiffField[] {
    const hidden = hiddenFieldsFor(item)
    const named = withCharterAnswers(item)
    const proposed = canonicalised(fieldsOf(named, supersededFields(item), hidden))
    const today = canonicalised(fieldsOf(current, new Set(), hidden))
    const emptied = emptiedFields(named)
    const keepsUnnamed = laysOverRecord(item)
    const keys = [...new Set([...today.keys(), ...proposed.keys()])]
    const fields = keys.map(key => {
        const before = today.get(key)
        const after = proposed.get(key) ?? (keepsUnnamed && !emptied.has(key) ? before : undefined)
        const status = statusOf(before, after)
        return {
            key,
            label: humanise(key),
            status,
            current: before ?? ABSENT_VALUE,
            proposed: after ?? ABSENT_VALUE,
        }
    })
    return fields.sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status))
}

/**
 * A task as it stands today, keyed the way a plan item keys the same things, so the two can be read
 * side by side. The definition lives on the task's current version; identity and wiring sit on the row.
 * The skill text comes with the single-task read (`TaskDetail`), so a proposed skill diffs against the
 * wording the task runs on now rather than reading as a field being added (#1282).
 */
export function currentTaskFields(task: TaskRow | TaskDetail): Record<string, unknown> {
    const version = task.currentVersion ?? { goal: '' }
    const skillMd = 'skillMd' in task && typeof task.skillMd === 'string' ? task.skillMd : null
    return {
        name: task.name ?? null,
        slug: task.slug,
        kind: task.kind,
        schedule: task.schedule ?? null,
        assigneeRole: task.assigneeRole ?? null,
        goal: version.goal,
        metric: version.metric ?? null,
        guardrails: version.guardrails ?? null,
        tools: version.tools ?? null,
        toolSlots: version.toolSlots ?? null,
        inputs: version.inputs ?? null,
        gates: version.gates ?? null,
        spend: version.spend ?? null,
        workspace: version.workspace ?? null,
        successors: version.successors ?? null,
        finishesOn: version.finishesOn ?? null,
        skillMd,
    }
}

/**
 * What approving an item does that its fields cannot say (#1297), in the CEO's words, or null. A task delete stops
 * the task and keeps its history rather than erasing it; a team delete only goes through once nobody is seated on it.
 * A team's roles and tasks are its members', so they never hold a delete up.
 */
export function planItemNote(item: PlanItem): string | null {
    if (item.type === 'delete_task') {
        return 'This task stops running; its history stays. Its past runs, the gates decided on them and their spend remain readable.'
    }
    if (item.type === 'delete_team') {
        return 'The team is deleted only if no member is still seated on it when the plan applies. Its roles and tasks belong to its members and go with them. Otherwise nothing is deleted, and the refusal names who is still on it.'
    }
    return null
}

/** A role as it stands today, keyed the way an `update_role` item keys the same things. */
export function currentRoleFields(role: Role): Record<string, unknown> {
    return { slug: role.slug, name: role.name }
}

/**
 * A team as it stands today, keyed the way an `update_team` item keys the same things. The charter is
 * flattened to its seven answers rather than kept as one blob, because a proposal names them one at a
 * time (`purpose`, `metric`, ...) — nested, every charter answer would read as a single changed field.
 */
export function currentTeamFields(team: Team): Record<string, unknown> {
    const charter = (team.charter ?? {}) as Partial<TeamCharter>
    return {
        slug: team.slug,
        name: team.name,
        status: team.status,
        lead: team.leadName ?? null,
        opportunity: charter.opportunity ?? null,
        purpose: charter.purpose ?? null,
        success: charter.success ?? null,
        metric: charter.metric ?? null,
        kit: charter.kit ?? null,
        roles: charter.roles ?? null,
        timeline: charter.timeline ?? null,
    }
}

/**
 * A member as it stands today, keyed the way an `update_member` item keys the same things. Roles and
 * teams are read as their slugs, which is how a proposal names them, so a membership the proposal
 * keeps does not read as a change simply because the record carries the whole row behind the slug.
 */
export function currentMemberFields(member: Member): Record<string, unknown> {
    return {
        name: member.name,
        email: member.email,
        persona: member.persona ?? null,
        roles: member.roles?.map(role => role.slug) ?? null,
        teams: member.teams?.map(team => team.slug) ?? null,
    }
}
